import React, { useContext, useMemo, useEffect, useState, useRef } from 'react';
import Tabs from '@lingxi-common-component/sirius-ui/Tabs';
import { api, apis, getIn18Text } from 'api';
import { WriteContext } from '@web-setting/Mail/components/CustomTemplate/template_add_modal';
import { useAppSelector } from '@web-common/state/createStore';
import WriteSearchContact from './writeSearchContact';
import WriteContact from './writeContact';
import WriteCustomerContact from './writeCustomerContact';
import WriteLeadsContact from './writeLeadsContact';
import styles from './writeSide.module.scss';
import RightSidebar, { RightSidebarScene } from '@web-mail/rightSidebar';
import { transContactItem2MailContactModel, transMailContactModel2ContactItem } from '@web-common/utils/contact_util';
import { useContactModel, useCustomerModel } from '@web-common/hooks/useContactModel';
import { zoneData, zoneVar } from '@web-mail/components/ReadMail/component/TimeZone/zone';
import SearchInput from '@web-common/components/UI/SiriusContact/selectSearchList/searchInput';
import useState2RM from '@web-mail/hooks/useState2ReduxMock';
const contactApi = api.requireLogicalApi(apis.contactApiImpl);
export const useHeightestPriorityEmail = () => {
    const { isMailTemplate } = useContext(WriteContext);
    const isOneRcpt = useAppSelector(state => state.mailReducer.currentMail.isOneRcpt);
    const receivers = useAppSelector(state => { var _a, _b; return (isMailTemplate ? (_a = state.mailTemplateReducer.mailTemplateContent) === null || _a === void 0 ? void 0 : _a.receiver : (_b = state.mailReducer.currentMail) === null || _b === void 0 ? void 0 : _b.receiver); });
    const selectedEmails = useAppSelector(state => (isMailTemplate ? state.tempContactReducer.selectedTags.emails : state.contactReducer.selectedTags.emails));
    const firstSelectedEmail = selectedEmails[0];
    const computedItem = useMemo(() => {
        if (!(receivers === null || receivers === void 0 ? void 0 : receivers.length)) {
            return {
                contactItem: undefined,
                selectedEmail: undefined,
            };
        }
        if (firstSelectedEmail) {
            const findItem = receivers.find(item => {
                return contactApi.doGetModelDisplayEmail(item === null || item === void 0 ? void 0 : item.contact) === firstSelectedEmail;
            });
            if (findItem) {
                return {
                    contactItem: transMailContactModel2ContactItem(findItem),
                    selectedEmail: firstSelectedEmail,
                };
            }
        }
        if (isOneRcpt) {
            const contactItem = transMailContactModel2ContactItem(receivers[0]);
            return {
                contactItem,
                selectedEmail: undefined,
            };
        }
        let email, ccEmail, bccEmail;
        receivers.forEach(item => {
            const contactItem = transMailContactModel2ContactItem(item);
            if (contactItem) {
                if (!email && item.mailMemberType === 'to') {
                    email = contactItem;
                }
                if (!email && !ccEmail && item.mailMemberType === 'cc') {
                    ccEmail = contactItem;
                }
                if (!email && !ccEmail && !bccEmail && item.mailMemberType === 'bcc') {
                    bccEmail = contactItem;
                }
            }
        });
        return {
            contactItem: email || ccEmail || bccEmail,
            selectedEmail: undefined,
        };
    }, [isOneRcpt, receivers, firstSelectedEmail]);
    return computedItem;
};
export const useZoneParamsByContact = () => {
    var _a, _b;
    const { isMailTemplate } = useContext(WriteContext);
    const isOneRcpt = useAppSelector(state => state.mailReducer.currentMail.isOneRcpt);
    const receivers = useAppSelector(state => { var _a, _b; return (isMailTemplate ? (_a = state.mailTemplateReducer.mailTemplateContent) === null || _a === void 0 ? void 0 : _a.receiver : (_b = state.mailReducer.currentMail) === null || _b === void 0 ? void 0 : _b.receiver); });
    const contactItem = useMemo(() => {
        if (!(receivers === null || receivers === void 0 ? void 0 : receivers.length)) {
            return undefined;
        }
        if (isOneRcpt) {
            return transMailContactModel2ContactItem(receivers[0]);
        }
        let email, ccEmail, bccEmail;
        receivers.forEach(item => {
            const contactItem = transMailContactModel2ContactItem(item);
            if (contactItem) {
                if (!email && item.mailMemberType === 'to') {
                    email = contactItem;
                }
                if (!email && !ccEmail && item.mailMemberType === 'cc') {
                    ccEmail = contactItem;
                }
                if (!email && !ccEmail && !bccEmail && item.mailMemberType === 'bcc') {
                    bccEmail = contactItem;
                }
            }
        });
        return email || ccEmail || bccEmail;
    }, [isOneRcpt, receivers]);
    const contact = useMemo(() => {
        if (!contactItem) {
            return undefined;
        }
        else {
            const contactModel = transContactItem2MailContactModel(contactItem);
            return contactModel.contact || undefined;
        }
    }, [contactItem]);
    const curAccount = useMemo(() => contact === null || contact === void 0 ? void 0 : contact._account, [contact]);
    const email = useMemo(() => (contact ? contactApi.doGetModelDisplayEmail(contact) : ''), [contact]);
    const contactId = useMemo(() => { var _a; return (_a = contact === null || contact === void 0 ? void 0 : contact.contact) === null || _a === void 0 ? void 0 : _a.id; }, [contact]);
    const reduxContact = useContactModel({ email, contactId, _account: curAccount });
    const orgData = useCustomerModel({
        customerId: (_a = reduxContact === null || reduxContact === void 0 ? void 0 : reduxContact.customerOrgModel) === null || _a === void 0 ? void 0 : _a.companyId,
        email,
        _account: curAccount,
        emailRole: (_b = reduxContact === null || reduxContact === void 0 ? void 0 : reduxContact.customerOrgModel) === null || _b === void 0 ? void 0 : _b.role,
    });
    const area = useMemo(() => orgData === null || orgData === void 0 ? void 0 : orgData.area, [orgData]);
    const country = useMemo(() => (area ? area.split('-')[1] : ''), [area]);
    const contactZone = useMemo(() => orgData === null || orgData === void 0 ? void 0 : orgData.zone, [orgData]);
    const zoneParams = useMemo(() => {
        const res = { label: '', zoneOffset: null };
        let onZone = null;
        const lang = typeof window !== 'undefined' ? window.systemLang : 'zh';
        if (contactZone) {
            onZone = zoneVar.find(i => i.value === contactZone);
        }
        else if (country) {
            onZone = zoneData.find(i => i.country === country);
        }
        if (onZone) {
            res.label = lang === 'en' ? onZone.timezoneEn : onZone.timezone;
            res.zoneOffset = onZone.zoneOffset;
        }
        return res;
    }, [contactZone, country]);
    return {
        zoneParams,
    };
};
const WriteSideContent = () => {
    const [active, setActive] = useState('myCustomerContact');
    const [edmShowContactDetail, setEdmShowContactDetail] = useState2RM('edmShowContactDetail');
    const { contactItem, selectedEmail } = useHeightestPriorityEmail();
    useEffect(() => {
        if (contactItem && contactItem.email === selectedEmail) {
            setEdmShowContactDetail(true);
        }
    }, [contactItem, selectedEmail]);
    const [searchValue, setSearchValue] = useState('');
    const selectListRef = useRef(null);
    const handleKeyDown = (e) => {
        var _a;
        (_a = selectListRef.current) === null || _a === void 0 ? void 0 : _a.handleInputKeyDown(e);
    };
    const handleEnter = (e) => {
        var _a;
        (_a = selectListRef.current) === null || _a === void 0 ? void 0 : _a.handleInputEnter(e);
    };
    const handleSearchChange = (e) => {
        setSearchValue(e.target.value);
    };
    return (React.createElement("div", { className: "writesidecontentWrap", style: { height: '100%', display: 'flex', flexDirection: 'column', minWidth: 350, width: 350 } }, edmShowContactDetail && contactItem ? (React.createElement(RightSidebar, { scene: RightSidebarScene.EmailPlus, noBorder: true, email: contactItem.email, name: contactItem.name, _account: contactItem._account, needGlobalSearch: false })) : (React.createElement(React.Fragment, null,
        React.createElement("div", { className: styles.searchInputWrap },
            React.createElement(SearchInput, { value: searchValue, onChange: handleSearchChange, onKeyDown: handleKeyDown, onPressEnter: handleEnter, autoFocus: true })),
        searchValue != '' ? (React.createElement("div", { style: { height: '100%' } },
            React.createElement(WriteSearchContact, { searchValue: searchValue, ref: selectListRef }))) : (React.createElement(Tabs, { activeKey: active, onChange: val => setActive(val), className: styles.writeSideContainer, tabBarGutter: 0, renderTabBar: (props, TabComp) => {
                return (React.createElement("div", { className: styles.writeSideTabBar },
                    React.createElement(TabComp, Object.assign({}, props))));
            } },
            React.createElement(Tabs.TabPane, { tab: React.createElement("span", { "data-test-id": "write-side-contact" }, getIn18Text('myCustomer')), key: "myCustomerContact" },
                React.createElement("div", { className: styles.writeSideContactWrap },
                    React.createElement(WriteCustomerContact, null))),
            React.createElement(Tabs.TabPane, { tab: React.createElement("span", { "data-test-id": "write-side-contact" }, getIn18Text('myClue')), key: "myClueContact" },
                React.createElement("div", { className: styles.writeSideContactWrap },
                    React.createElement(WriteLeadsContact, null))),
            React.createElement(Tabs.TabPane, { tab: React.createElement("span", { "data-test-id": "write-side-contact" }, getIn18Text('TONGXUNLULXR')), key: "writeContact" },
                React.createElement(WriteContact, { showSearch: false }))))))));
};
const WriteSide = () => {
    const { isMailTemplate } = useContext(WriteContext);
    return process.env.BUILD_ISEDM && !isMailTemplate ? React.createElement(WriteSideContent, null) : React.createElement(WriteContact, null);
};
export default WriteSide;
